import { NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Geolocation } from '@capacitor/geolocation';
import { AlertFacade } from '@curbnturf/alert';
import { BoondockFacade } from '@curbnturf/boondock-listing/src/lib/+state/boondock.facade';
import { PhoneMaskDirective } from '@curbnturf/cnt-masks';
import { AccessRoute, IAccessRoute, IBoondock, ILatLon, IListing, ISite } from '@curbnturf/entities';
import {
  accessRouteFormBuilder,
  CheckedComponent,
  DisplayValidationErrorsDirective,
  formatAddressFormValue,
  formatCoordinatesFormValue,
  formatWaypointFormValue,
  IAccessRouteForm,
  IAddressForm,
  ICoordinatesForm,
  ILandOwnerForm,
  IWaypointForm,
  ListingForm,
} from '@curbnturf/form-helpers';
import { boondockComplete, listingComplete, siteComplete } from '@curbnturf/helpers';
import { LandOwnerComponent } from '@curbnturf/host-forms';
import { RvRequirementsComponent } from '@curbnturf/host-forms/src/lib/rv-requirements/rv-requirements.component';
import { ListingFacade } from '@curbnturf/listing';
import { AddWaypointComponent } from '@curbnturf/listing-edit/src/lib/add-waypoint/add-waypoint.component';
import { LocationComponent } from '@curbnturf/location-interface/src/lib/location/location.component';
import { CoordinatesMapComponent } from '@curbnturf/map/src/lib/coordinates-map/coordinates-map.component';
import { MapModalComponent } from '@curbnturf/map/src/lib/map-modal/map-modal.component';
import { SiteFacade } from '@curbnturf/site';
import { IonicModule, ModalController } from '@ionic/angular';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { ListingEditSiteSelectionComponent } from '../listing-edit-site-selection/listing-edit-site-selection.component';

@Component({
  selector: 'curbnturf-directions',
  templateUrl: './directions.component.html',
  styleUrls: ['./directions.component.scss'],
  standalone: true,
  imports: [
    AddWaypointComponent,
    CheckedComponent,
    CoordinatesMapComponent,
    DisplayValidationErrorsDirective,
    FormsModule,
    IonicModule,
    LocationComponent,
    MapModalComponent,
    NgIf,
    ReactiveFormsModule,
    RvRequirementsComponent,
    ListingEditSiteSelectionComponent,
    LandOwnerComponent,
    PhoneMaskDirective,
  ],
})
export class DirectionsComponent implements OnChanges, OnDestroy, OnInit {
  @ViewChild('locationComponent')
  locationComponent: LocationComponent;
  @ViewChild('landOwnerComponent')
  landOwnerComponent: LandOwnerComponent;

  @Input() site: ISite;
  @Input() listing: IListing;
  @Input() boondock: IBoondock;

  boondockForm: FormGroup<{
    phone: FormControl<string | null>;
    email: FormControl<string | null>;
    url: FormControl<string | null>;
  }>;

  addressForm: FormGroup<{
    address: FormGroup<IAddressForm>;
    coordinates: FormGroup<ICoordinatesForm>;
    landOwner: FormGroup<ILandOwnerForm>;
  }>;

  accessRoutesForm: FormArray<FormGroup<IAccessRouteForm>>;

  siteComplete = siteComplete;
  listingFormObject: ListingForm;

  openModalId?: string;

  get address() {
    if (this.addressForm) {
      return this.addressForm.controls.address;
    }
    return;
  }

  get accessRoute() {
    if (this.accessRoutesForm) {
      let accessRoute = this.accessRoutesForm.at(0) as FormGroup<IAccessRouteForm>;
      if (!accessRoute) {
        accessRoute = this.fb.group(this.listingFormObject.accessRouteGroup());
        this.accessRoutesForm.push(accessRoute);
      }

      return accessRoute;
    }

    return;
  }

  get coordinates(): FormGroup<ICoordinatesForm> | undefined {
    if (this.addressForm) {
      return this.addressForm.controls.coordinates;
    }

    return;
  }

  get directions(): FormControl<string | null> | undefined {
    if (this.accessRoute) {
      return this.accessRoute.controls.directions;
    }

    return;
  }

  get intermediateWaypoints() {
    if (this.accessRoute) {
      return this.accessRoute.get('intermediateWaypoints') as FormArray<FormGroup<IWaypointForm>>;
    }

    return;
  }

  get landOwner(): FormGroup<ILandOwnerForm> | undefined {
    if (this.addressForm) {
      return this.addressForm.controls.landOwner;
    }

    return;
  }

  get landOwnerAddress() {
    if (this.addressForm) {
      return this.addressForm.controls.landOwner.controls.address;
    }
    return;
  }

  get phone() {
    if (this.boondockForm) {
      return this.boondockForm.controls.phone;
    }
    return;
  }

  get email() {
    if (this.boondockForm) {
      return this.boondockForm.controls.email;
    }
    return;
  }

  get url() {
    if (this.boondockForm) {
      return this.boondockForm.controls.url;
    }
    return;
  }

  get pointList(): ILatLon[] {
    const points: ILatLon[] = [];
    let startingPoint: ILatLon | undefined;
    if (this.startingPoint) {
      startingPoint = formatCoordinatesFormValue(this.startingPoint);

      if (startingPoint) {
        points.push(startingPoint);
      }
    }

    if (this.intermediateWaypoints) {
      const waypoints = this.intermediateWaypoints.controls.map((waypoint) => formatWaypointFormValue(waypoint));

      points.push(...(waypoints.filter((waypoint) => waypoint) as ILatLon[]));
    }

    return points;
  }

  get sites(): ISite[] {
    if (this.listing?.sites) {
      return this.listing.sites;
    }
    return [];
  }

  get startingPoint(): FormGroup<ICoordinatesForm> | undefined {
    if (this.accessRoute) {
      return this.accessRoute.controls.startingPoint;
    }

    return;
  }

  pointListSubject: Subject<ILatLon[]>;
  pointList$: Observable<ILatLon[]>;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private modalController: ModalController,
    private siteFacade: SiteFacade,
    private listingFacade: ListingFacade,
    private boondockFacade: BoondockFacade,
    private alertFacade: AlertFacade,
  ) {
    this.pointListSubject = new BehaviorSubject(this.pointList);
    this.pointList$ = this.pointListSubject.asObservable();
    this.listingFormObject = new ListingForm(fb);
  }

  ngOnInit() {
    this.createForms();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.site?.currentValue) {
      this.pointListSubject.next(this.pointList);
    }

    if (changes.boondock?.currentValue) {
      this.pointListSubject.next(this.pointList);
    }

    this.createForms();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  createForms() {
    this.addressForm = this.fb.group({
      coordinates: this.fb.group<ICoordinatesForm>({
        lat: this.fb.control(null),
        lon: this.fb.control(null),
      }),
      landOwner: this.fb.group<ILandOwnerForm>({
        id: this.fb.control<number | null>(null),
        self: this.fb.control(null),
        private: this.fb.control(null),
        firstName: this.fb.control(null),
        lastName: this.fb.control(null),
        phone: this.fb.control(null),
        address: this.fb.group<IAddressForm>({
          address: this.fb.control(null),
          city: this.fb.control(null),
          state: this.fb.control(null),
          zipcode: this.fb.control(null),
        }),
        represent: this.fb.control(null),
      }),
      address: this.fb.group<IAddressForm>({
        address: this.fb.control(null),
        city: this.fb.control(null),
        state: this.fb.control(null),
        zipcode: this.fb.control(null),
      }),
    });

    this.boondockForm = this.fb.group({
      phone: this.fb.control<string | null>(null),
      email: this.fb.control<string | null>(null),
      url: this.fb.control<string | null>(null),
    });

    this.accessRoutesForm = this.fb.array<FormGroup<IAccessRouteForm>>([]);

    if (this.listing?.id) {
      this.addressForm.patchValue({
        coordinates: {
          lat: this.listing.coordinates?.lat,
          lon: this.listing.coordinates?.lon,
        },
        landOwner: {
          id: this.listing.landOwner?.id,
          self: this.listing.landOwner?.self,
          private: this.listing.landOwner?.private,
          firstName: this.listing.landOwner?.firstName,
          lastName: this.listing.landOwner?.lastName,
          phone: this.listing.landOwner?.phone,
          address: {
            address: this.listing.landOwner?.address?.address,
            city: this.listing.landOwner?.address?.city,
            state: this.listing.landOwner?.address?.state,
            zipcode: this.listing.landOwner?.address?.zipcode,
          },
          represent: this.listing.landOwner?.represent,
        },
        address: {
          address: this.listing.address?.address,
          city: this.listing.address?.city,
          state: this.listing.address?.state,
          zipcode: this.listing.address?.zipcode,
        },
      });

      if (this.site?.accessRoutes?.length) {
        this.site.accessRoutes.forEach((access) => this.accessRoutesForm.push(accessRouteFormBuilder(this.fb, access)));
      } else {
        this.accessRoutesForm.push(this.fb.group(this.listingFormObject.accessRouteGroup()));
      }
    }

    if (this.boondock?.id) {
      this.addressForm.patchValue({
        coordinates: {
          lat: this.boondock.coordinates?.lat,
          lon: this.boondock.coordinates?.lon,
        },
        landOwner: {
          id: this.boondock.landOwner?.id,
          self: this.boondock.landOwner?.self,
          private: this.boondock.landOwner?.private,
          firstName: this.boondock.landOwner?.firstName,
          lastName: this.boondock.landOwner?.lastName,
          phone: this.boondock.landOwner?.phone,
          address: {
            address: this.boondock.landOwner?.address?.address,
            city: this.boondock.landOwner?.address?.city,
            state: this.boondock.landOwner?.address?.state,
            zipcode: this.boondock.landOwner?.address?.zipcode,
          },
          represent: this.boondock.landOwner?.represent,
        },
        address: {
          address: this.boondock.address?.address,
          city: this.boondock.address?.city,
          state: this.boondock.address?.state,
          zipcode: this.boondock.address?.zipcode,
        },
      });

      this.boondockForm.patchValue({
        phone: this.boondock.phone,
        email: this.boondock.email,
        url: this.boondock.url,
      });

      this.boondock?.accessRoutes?.forEach((access) =>
        this.accessRoutesForm.push(accessRouteFormBuilder(this.fb, access)),
      );
    }

    this.subscriptions.push(
      this.siteFacade.selectedOnce$.subscribe((site) => {
        if (site) {
          this.site = site;
        }
      }),
    );

    this.subscriptions.push(
      this.addressForm.valueChanges
        .pipe(
          tap(() => {
            this.addressForm.markAsTouched();
            this.addressForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => {
          this.updateAddressAndAccess();
        }),
      this.accessRoutesForm.valueChanges
        .pipe(
          tap(() => {
            this.accessRoutesForm.markAsTouched();
            this.accessRoutesForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => {
          this.updateAccessRoutes();
        }),
      this.boondockForm.valueChanges
        .pipe(
          tap(() => {
            this.boondockForm.markAsTouched();
            this.boondockForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => {
          this.updateBoondockForm();
        }),
    );
  }

  updateAddressAndAccess() {
    this.locationComponent.validate();
    this.landOwnerComponent.validate();

    if (this.addressForm.valid) {
      // Update the user record with the following data
      if (this.listing?.id) {
        this.listingFacade.updateLocation(this.listing.id, {
          address: formatAddressFormValue(this.addressForm.controls.address),
          coordinates: formatCoordinatesFormValue(this.addressForm.controls.coordinates),
          landOwner: ListingForm.formatLandOwnerFormValue(this.addressForm.controls.landOwner),
        });
      } else if (this.boondock?.id) {
        this.boondock = {
          ...this.boondock,
          address: formatAddressFormValue(this.addressForm.controls.address),
          coordinates: formatCoordinatesFormValue(this.addressForm.controls.coordinates),
          landOwner: ListingForm.formatLandOwnerFormValue(this.addressForm.controls.landOwner),
        };

        this.boondockFacade.update(this.boondock);
      }
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  updateBoondockForm() {
    if (this.boondockForm.valid) {
      if (this.boondock?.id) {
        this.boondock = {
          ...this.boondock,
          phone: this.boondockForm.controls?.phone?.value || undefined,
          email: this.boondockForm.controls?.email?.value || undefined,
          url: this.boondockForm.controls?.url?.value || undefined,
        };

        this.boondockFacade.update(this.boondock);
      }
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  updateAccessRoutes() {
    if (this.accessRoutesForm.valid) {
      const accessRoutes = this.accessRoutesForm.controls.map((el): IAccessRoute => {
        const accessRoute = new AccessRoute({
          id: el.controls.id.value ?? undefined,
          name: el.controls.name.value ?? undefined,
          description: el.controls.description.value ?? undefined,
          directions: el.controls.directions.value ?? undefined,
          maxHeight: el.controls.maxHeight.value ?? undefined,
          maxWidth: el.controls.maxWidth.value ?? undefined,
          maxLength: el.controls.maxLength.value ?? undefined,
          minClearance: el.controls.minClearance.value ?? undefined,
          intermediateWaypoints: (el.controls.intermediateWaypoints?.controls || []).map((waypointForm) => {
            return {
              name: waypointForm?.controls?.name.value as string,
              lat: waypointForm?.controls?.lat.value as number,
              lon: waypointForm?.controls?.lon.value as number,
            };
          }),
          roadCondition: {
            offRoad: el.controls.roadCondition.controls.offRoad.value ?? false,
            highClearance: el.controls.roadCondition.controls.highClearance.value ?? false,
            paved: el.controls.roadCondition.controls.paved.value ?? false,
            poorGravel: el.controls.roadCondition.controls.poorGravel.value ?? false,
            smoothGravel: el.controls.roadCondition.controls.smoothGravel.value ?? false,
            offRoadDescription: el.controls.roadCondition.controls.offRoadDescription.value ?? '',
            highClearanceDescription: el.controls.roadCondition.controls.highClearanceDescription.value ?? '',
            pavedDescription: el.controls.roadCondition.controls.pavedDescription.value ?? '',
            poorGravelDescription: el.controls.roadCondition.controls.poorGravelDescription.value ?? '',
            smoothGravelDescription: el.controls.roadCondition.controls.smoothGravelDescription.value ?? '',
          },
        });

        if (el.controls.startingPoint?.controls?.lat?.value || el.controls.startingPoint?.controls?.lon?.value) {
          accessRoute.startingPoint = {
            lat: el.controls.startingPoint?.controls?.lat?.value as number,
            lon: el.controls.startingPoint?.controls?.lon?.value as number,
          };
        }

        return accessRoute;
      });

      if (this.site?.id) {
        // Update the user record with the following data
        this.siteFacade.updateAccessRoutes(accessRoutes);
      } else if (this.boondock?.id) {
        this.boondock = { ...this.boondock, accessRoutes };
        this.boondockFacade.update(this.boondock);
      }
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  async getCoordinatesFromMap() {
    const waypointModal = await this.modalController.create({
      component: MapModalComponent,
      componentProps: {
        pins$: this.pointList$,
        center: this.startingPoint?.value?.lat ? this.startingPoint.value : this.coordinates?.value,
        draggable: false,
        text: 'Select starting location',
        locationClicked: (coordinates: ILatLon) => this.locationClicked(coordinates),
      },
    });
    await waypointModal.present();
  }

  locationClicked(coordinates: ILatLon) {
    this.setCoordinates(coordinates);
  }

  async setCoordinatesToCurrent() {
    const location = await Geolocation.getCurrentPosition();
    this.setCoordinates({
      lat: location.coords.latitude,
      lon: location.coords.longitude,
    });
  }

  private setCoordinates(coordinates: ILatLon) {
    if (this.accessRoute) {
      const startingPoint = this.accessRoute.get('startingPoint');
      if (startingPoint) {
        startingPoint.setValue(coordinates);
      }
      this.accessRoute.markAsDirty();
    }

    this.pointListSubject.next(this.pointList);
  }

  protected readonly listingComplete = listingComplete;

  landOwnerCompleted(address: IListing | IBoondock): boolean {
    if (this.listing?.id) {
      return listingComplete.landOwnerCompleted(address as IListing);
    }

    if (this.boondock?.id) {
      return boondockComplete.landOwnerCompleted(address as IBoondock);
    }

    return false;
  }
}
